import { useEffect, useMemo, useState } from 'react'

import { useUpdateDevice } from './useUpdateDevice'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  useFacilityDevicesByFloorsQuery,
  useFacilityFloorPlanQuery,
} from '@/graphql/generated/hooks'
import { DeviceType, UrlContent } from '@/graphql/generated/schemas'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { DeviceI, DevicePlacementContextI, FloorI, Point } from '../types/types'
import { getCompanionPlacements } from '../utils/associatedDevicePlacement'
import { getIsFloorPlanKnownEmpty } from '../utils/floorPlanAvailability'
import {
  filterDevicesWithAssociations,
  getDeviceFloorIds,
  mapDevices,
} from '../utils/utils'

const ASSOCIATED_DEVICES_TOAST_DURATION = 5000

export const useFloorData = (
  floorId: string,
  floors: FloorI[],
  facilityId: string,
  isEditMode: boolean,
  types: DeviceType[],
  nameLikeFilter?: string,
  shouldShowAssociatedDevices?: boolean
) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [devices, setDevices] = useState<DeviceI[]>([])
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isDevicesPanel = drawerOpen === 'devices'

  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts({
    toastDuration: ASSOCIATED_DEVICES_TOAST_DURATION,
  })
  const { shouldEnableUpdateDevice } = usePermissions()
  const isAssociatedPlacementEnabled =
    useFeatureFlag(FeatureFlag.floorPlanAssociatedDevicePlacement) ||
    useFeatureFlag(FeatureFlag.development)

  const { updateDevice, updateDevicePositions } = useUpdateDevice(floorId)
  const shouldFilterByNameLike = isDevicesPanel && !shouldShowAssociatedDevices
  const {
    data: devicesData,
    loading: isDevicesLoading,
    refetch: refetchDevices,
  } = useFacilityDevicesByFloorsQuery({
    fetchPolicy: 'network-only',
    skip: facilityId === undefined,
    variables: {
      floorId: floorId,
      facilityId: facilityId,
      types: types,
      floorIds: getDeviceFloorIds(floorId, floors, types),
      ...(shouldFilterByNameLike && { nameLike: nameLikeFilter || undefined }),
    },
  })
  const {
    data: floorPlanData,
    loading: isFloorPlanLoading,
    error: floorPlanError,
  } = useFacilityFloorPlanQuery({
    skip: facilityId === undefined,
    variables: {
      floorId: floorId,
      facilityId: facilityId,
    },
  })

  // Derived from the live query result rather than the imageUrl state below,
  // which can hold the previous floor's URL mid-switch (ENG-33744 review).
  const isFloorPlanKnownEmpty = getIsFloorPlanKnownEmpty(
    isFloorPlanLoading,
    floorPlanError,
    floorPlanData
  )

  const onFloorPlanContainerClick = () => {
    setDevices((currentData) =>
      currentData.map((item) => {
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onDeviceClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            isEditMode: !item.isEditMode,
          }
        }
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onMoveCheckPointClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            editCheckpointPos: !item.editCheckpointPos,
          }
        }
        return { ...item, editCheckpointPos: false }
      })
    )
  }

  const onDeviceUpdated = (data: DeviceI, positionValid: boolean) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === data.id) {
          return {
            ...data,
            invalidPosition: !positionValid,
          }
        }
        return item
      })
    )
    updateDevice(data, positionValid, false)
  }

  const onDeviceReleased = (data: DeviceI, positionValid: boolean) => {
    if (!positionValid) {
      setDevices((currentData) =>
        currentData.map((item) => {
          if (item.id === data.id) {
            return {
              ...item,
              isEditMode: false,
              placed: false,
              editCheckpointPos: false,
            }
          }
          return { ...item, isEditMode: false, editCheckpointPos: false }
        })
      )
    }
    updateDevice(data, positionValid, true)
  }

  const onDeviceDropped = (
    draggedDeviceId: string,
    position: Point,
    placementContext?: DevicePlacementContextI
  ) => {
    const [deviceX, deviceY] = position
    const deviceData = devices.find((device) => device.id === draggedDeviceId)

    // Placing associated devices only makes sense on a device's first placement: nudging an
    // already placed device must not drag its unplaced counterparts onto the floor plan again.
    // Without update permission nothing persists, so a toast would claim work that never
    // happened.
    const shouldPlaceAssociatedDevices =
      isAssociatedPlacementEnabled &&
      isDevicesPanel &&
      shouldEnableUpdateDevice &&
      !deviceData?.placed &&
      !!placementContext?.mapImageSize

    const companionPlacements = shouldPlaceAssociatedDevices
      ? getCompanionPlacements({
          devices,
          draggedDeviceId,
          dropPosition: position,
          iconSize: placementContext.iconSize,
          mapImageSize: placementContext.mapImageSize,
        })
      : []
    const companionsById = new Map(
      companionPlacements.map((placement) => [placement.deviceId, placement])
    )

    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === draggedDeviceId) {
          return {
            ...item,
            x: deviceX,
            y: deviceY,
            isEditMode: true,
            placed: true,
            invalidPosition: false,
            checkpoint: {
              ...item.checkpoint,
              floor: {
                ...item.checkpoint.floor,
                id: floorId,
              },
            },
          }
        }
        const companion = companionsById.get(item.id)
        // Re-check `placed` against the current state rather than the render it was resolved
        // from, so a device placed in the meantime is never moved.
        if (companion && !item.placed) {
          return {
            ...item,
            x: companion.x,
            y: companion.y,
            // a companion camera is turned to face the device it is associated with
            rotation: companion.rotation ?? item.rotation,
            distance: companion.distance ?? item.distance,
            isEditMode: false,
            editCheckpointPos: false,
            placed: true,
            invalidPosition: false,
          }
        }
        return {
          ...item,
          isEditMode: false,
          editCheckpointPos: false,
          invalidPosition: false,
        }
      })
    )
    if (deviceData) {
      updateDevice({ ...deviceData, x: deviceX, y: deviceY }, true, true)
    }

    if (companionPlacements.length === 0) return

    showSuccess(t('floors:plan_associated_devices_placed_success'))
    trackEvent('Floor Plan - Associated Devices Auto Placed', {
      facility_id: facilityId,
      floor_id: floorId,
      dragged_device_id: draggedDeviceId,
      associated_device_count: companionPlacements.length,
    })
    updateDevicePositions(
      companionPlacements.map((placement) => {
        const companion = devices.find(
          (device) => device.id === placement.deviceId
        )
        return {
          ...companion,
          x: placement.x,
          y: placement.y,
          rotation: placement.rotation ?? companion.rotation,
          distance: placement.distance ?? companion.distance,
        }
      })
    ).then((hasPartialFailure) => {
      if (hasPartialFailure) {
        showError(t('floors:plan_associated_devices_placed_error'))
      }
    })
  }

  useEffect(() => {
    if (!isDevicesLoading && devicesData) {
      let mappedDevices = mapDevices(devicesData)

      const shouldFilterByAssociateDevices =
        isDevicesPanel && shouldShowAssociatedDevices
      // If enabled, Filter devices to include their associated devices
      if (shouldFilterByAssociateDevices) {
        mappedDevices = filterDevicesWithAssociations(
          mappedDevices,
          nameLikeFilter,
          // Searching for a door only surfaces its cameras once the door side of a connection
          // is read directly, which is what makes search -> drag place associated devices.
          { shouldUseConnectionCameraIds: isAssociatedPlacementEnabled }
        )
      }

      setDevices(mappedDevices)
    }
  }, [
    devicesData,
    isDevicesLoading,
    isDevicesPanel,
    isAssociatedPlacementEnabled,
    nameLikeFilter,
    shouldShowAssociatedDevices,
  ])

  useEffect(() => {
    if (!isFloorPlanLoading && floorPlanData) {
      const floorPlanImage = floorPlanData?.facility?.floors?.edges[0]?.node
        ?.floorPlan?.image as UrlContent
      setImageUrl(floorPlanImage?.url)
    }
  }, [floorPlanData, isFloorPlanLoading])

  useEffect(() => {
    setDevices((currentData) =>
      currentData.map((item) => ({
        ...item,
        isEditMode: false,
        editCheckpointPos: false,
      }))
    )
  }, [isEditMode])

  const checkpointDevices = useMemo(
    () => devices?.filter((d) => d.deviceType === DeviceType.Checkpoint),
    [devices]
  )

  return {
    facilityId,
    imageUrl,
    isFloorPlanKnownEmpty,
    devices,
    checkpointDevices,
    loading: isDevicesLoading,
    onDeviceClicked,
    onFloorPlanContainerClick,
    onDeviceUpdated,
    onDeviceReleased,
    onDeviceDropped,
    onMoveCheckPointClicked,
    refetchDevices,
  }
}
